/* Trạng thái trong RAM của máy — nguồn duy nhất cho UI.
   Nguyên tắc docs/03-KIEN-TRUC.md mục 5: quét tra RAM (0 ms), ghi lên DB chạy nền.

   S.orders[key] = order JSON từ kho.order_json():
     { id, key, channel, orderId, tracking, carrier, statusRaw, subStatus, statusGroup,
       isReturn, orderDate, sourceFile, pickState, pickedAt, pickedMay, outState, outAt,
       outMay, note, claimedMay, claimedAt, updatedAt,
       items:[{idx, barcode, barcodeSrc, name, nameFull, variation, qty, picked, isSub, subName}] }
   Khác v4: goc + lamviec gộp làm một; item.name của dòng thay thế = subName. */
import * as K from './core/index.js';
import { sb, rpc } from './lib/supabase.js';
import { $, log } from './lib/ui.js';

export const S = {
  may: null,            // tên máy đang đăng nhập ('Máy 1')
  orders: {},           // key → order
  idx: {},              // upper(tracking) → [key]
  counts: {},           // 'SHOPEE|XONG' → n  (đơn XONG/HUY không tải về RAM, chỉ đếm)
  sum: null,            // K.summarize(orders) — đơn đang xử lý
  loadedAt: null,
  sound: true,
  ex: null
};

/* ---------- tải đầu ca ---------- */
export async function loadActive() {
  setSync('busy', 'đang tải…');
  const { data, ms } = await rpc('load_active');
  S.may = data.may;
  S.orders = {};
  (data.orders || []).forEach((o) => { S.orders[o.key] = o; });
  S.counts = data.counts || {};
  S.loadedAt = new Date();
  reindex();
  setSync('ok', 'đồng bộ ' + new Date().toLocaleTimeString('vi-VN') + ' · ' + ms + ' ms');
  log('Tải ' + Object.keys(S.orders).length + ' đơn đang xử lý từ máy chủ (' + ms + ' ms).', 'ok');
  return data;
}

export function reindex() {
  S.idx = K.buildTrackingIndex(S.orders);
  S.sum = K.summarize(S.orders);
}

export function hasData() { return Object.keys(S.orders).length > 0 || Object.keys(S.counts).length > 0; }

/* Tổng đơn kể cả XONG/HUY đang nằm trên máy chủ (không tải về RAM) */
export function totalCounts() {
  const g = { MOI: 0, CAN_GIAO: 0, XONG: 0, HUY: 0, KHAC: 0 };
  const byChannel = { SHOPEE: 0, TIKTOK: 0 };
  const matrix = {};
  let total = 0;
  Object.keys(S.counts).forEach((k) => {
    const [c, gr] = k.split('|'); const n = S.counts[k] || 0;
    g[gr] = (g[gr] || 0) + n; byChannel[c] = (byChannel[c] || 0) + n; matrix[k] = n; total += n;
  });
  return { total, byGroup: g, byChannel, matrix };
}

/* ---------- item helpers (thay itemAt/statOf của v4 trên cấu trúc gộp) ---------- */
export function itemView(it) {
  return {
    barcode: it.barcode || '',
    name: it.isSub ? (it.subName || it.name || '') : (it.name || ''),
    qty: it.isSub ? 0 : (it.qty || 0),
    picked: it.picked || 0,
    isSub: !!it.isSub
  };
}
export function statOf(o) {
  let short = false, over = false, sub = false, missing = 0, overQty = 0, picked = 0;
  (o.items || []).forEach((raw) => {
    const it = itemView(raw);
    picked += it.picked;
    if (it.isSub) { sub = true; return; }
    if (it.picked < it.qty) { short = true; missing += it.qty - it.picked; }
    if (it.picked > it.qty) { over = true; overQty += it.picked - it.qty; }
  });
  return { short, over, sub, missing, overQty, picked };
}
export function statLabel(st) {
  const t = [];
  if (st.short) t.push('Thiếu hàng');
  if (st.sub) t.push('Đổi hàng');
  if (st.over) t.push('Nhặt dư');
  return t.length ? t.join(' + ') : 'Hoàn thành';
}

/* ---------- tra cứu cục bộ (0 ms) ---------- */
export function findByCode(code) {
  const v = String(code || '').toUpperCase();
  let keys = S.idx[v] || [];
  let viaOid = false;
  if (!keys.length) {
    keys = Object.keys(S.orders).filter((k) => S.orders[k].orderId.toUpperCase() === v);
    viaOid = keys.length > 0;
  }
  return { keys, viaOid };
}

/* ---------- mutation cục bộ (RAM) — server nhận qua hàng đợi ---------- */
export function upsertLocal(o) {
  S.orders[o.key] = o; reindex();
}
export function removeLocal(key) {
  delete S.orders[key]; reindex();
}
export function localFinishPick(key, note) {
  const o = S.orders[key]; if (!o) return;
  o.pickState = 'done'; o.pickedAt = new Date().toISOString(); o.pickedMay = S.may;
  o.note = note || ''; o.claimedMay = null; o.claimedAt = null;
  reindex();
}
export function localResetPick(key) {
  const o = S.orders[key]; if (!o) return;
  o.items = (o.items || []).filter((it) => !it.isSub).map((it) => Object.assign({}, it, { picked: 0, barcode: it.barcodeSrc || it.barcode }));
  o.pickState = 'pending'; o.pickedAt = null; o.pickedMay = null; o.note = '';
  reindex();
}
export function localOut(key) {
  const o = S.orders[key]; if (!o) return;
  o.outState = 'done'; o.outAt = new Date().toISOString(); o.outMay = S.may;
}
export function localUndoOut(key) {
  const o = S.orders[key]; if (!o) return;
  o.outState = 'pending'; o.outAt = null; o.outMay = null;
}

/* Dòng bảng kho.orders + kho.order_items (snake_case) → dạng order JSON như load_active */
export function rowToOrder(r) {
  const p = (x) => String(x).padStart(2, '0');
  let od = '';
  if (r.order_date) { const d = new Date(r.order_date); od = d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + ' ' + p(d.getHours()) + ':' + p(d.getMinutes()); }
  return {
    id: r.id, key: r.channel + '|' + r.order_id, channel: r.channel, orderId: r.order_id,
    tracking: r.tracking, carrier: r.carrier, statusRaw: r.status_raw, subStatus: r.sub_status,
    statusGroup: r.status_group, isReturn: r.is_return, orderDate: od, sourceFile: r.source_file,
    pickState: r.pick_state, pickedAt: r.picked_at, pickedMay: r.picked_may,
    outState: r.out_state, outAt: r.out_at, outMay: r.out_may, note: r.note,
    claimedMay: r.claimed_may, claimedAt: r.claimed_at, updatedAt: r.updated_at,
    items: (r.order_items || []).slice().sort((a, b) => a.idx - b.idx).map((it) => ({
      idx: it.idx, barcode: it.barcode, barcodeSrc: it.barcode_src, name: it.name, nameFull: it.name_full,
      variation: it.variation, qty: it.qty, picked: it.picked, isSub: it.is_sub, subName: it.sub_name
    }))
  };
}
export async function fetchOrder(id) {
  const { data, error } = await sb.from('orders').select('*, order_items(*)').eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  return data ? rowToOrder(data) : null;
}

/* ---------- realtime: máy khác pick/out → RAM máy này ---------- */
const RT = { chan: null, onChange: null, skip: () => false };
export function startRealtime(onChange, skipFn) {
  RT.onChange = onChange; if (skipFn) RT.skip = skipFn;
  if (RT.chan) return;
  RT.chan = sb.channel('kho-orders')
    .on('postgres_changes', { event: '*', schema: 'kho', table: 'orders' }, async (msg) => {
      try {
        if (msg.eventType === 'DELETE') {
          const old = msg.old || {};
          const key = old.channel && old.order_id ? old.channel + '|' + old.order_id : Object.keys(S.orders).find((k) => S.orders[k].id === old.id);
          if (key && S.orders[key]) { removeLocal(key); if (RT.onChange) RT.onChange('delete', key); }
          return;
        }
        const row = msg.new; if (!row) return;
        const key = row.channel + '|' + row.order_id;
        if (RT.skip(key)) {                             // đơn đang mở / đang chờ gửi trên máy này: không đè RAM, chỉ báo
          if (RT.onChange) RT.onChange('skipped', key, { orderId: row.order_id, pickState: row.pick_state, pickedMay: row.picked_may, outState: row.out_state, outMay: row.out_may });
          return;
        }
        const cur = S.orders[key];
        if (cur && cur.updatedAt && row.updated_at && new Date(row.updated_at) <= new Date(cur.updatedAt)) return;
        const o = await fetchOrder(row.id);
        if (!o) return;
        const relevant = ['MOI', 'CAN_GIAO', 'KHAC'].includes(o.statusGroup) || o.pickState === 'done' || o.outState === 'done';
        if (relevant) upsertLocal(o); else if (cur) removeLocal(key);
        if (RT.onChange) RT.onChange(msg.eventType.toLowerCase(), key, o);
      } catch (e) { log('Realtime: ' + e.message, 'wr'); }
    })
    .subscribe((status) => {
      if (status === 'SUBSCRIBED') log('Realtime: đã nối, máy khác thao tác sẽ thấy ngay.', 'ok');
      else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') log('Realtime: ' + status + ' — sẽ tự nối lại.', 'wr');
    });
}

/* ---------- chip đồng bộ ---------- */
export function setSync(state, text) {
  const c = $('#syncChip'), l = $('#syncLbl');
  if (!c) return;
  c.className = 'sync ' + state; l.textContent = text;
}
window.addEventListener('offline', () => setSync('off', 'mất mạng'));
window.addEventListener('online', () => setSync('ok', 'có mạng lại'));
